import { PublicLayout } from '../layout/PublicLayout';
import { HeroSection } from '../sections/HeroSection';
import { RaiseSection } from '../sections/RaiseSection';
import { ProblemResearchSection } from '../sections/ProblemResearchSection';
import { FeaturesSection } from '../sections/FeaturesSection';
import { ComplianceSection } from '../sections/ComplianceSection';
import { SystemLearnsSection } from '../sections/SystemLearnsSection';
import { BlogSection } from '../sections/BlogSection';
import { CTASection } from '../sections/CTASection';
import { CountdownTimer, LIVE_GATE } from '@/components/blog/CountdownTimer';
import {
  CompanyBuilderFlow,
  DepartmentCards,
  CostComparisonChart,
  BotGraduationTimeline,
  ROIGrowthChart,
  FundingStagesExplorer,
} from '../sections/visualizations';
import { useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { markLandingMount } from '@/utils/ftueClock';

export const LandingPage = () => {
  // project100 D-015: page KPI is time-to-entry — clock starts at mount.
  useEffect(() => {
    markLandingMount();
  }, []);

  useSEO({
    title: 'Raisy - AI Workforce Platform | A Department of AI, Directed by Your People',
    description:
      "Raisy reads your company and deploys a governed AI workforce — fundraising, sales, recruiting, support — with one shared memory that never forgets, human approval at every gate, and a full audit trail. A department's workload — with your people at every decision gate. Claude is the brain; Raisy is the company it works for. Early access open.",
    keywords:
      'AI workforce, AI employees, virtual team, Claude alternative, fundraising automation, sales automation, recruiting automation, AI bots, AI department, shared memory, autonomous workforce, enterprise AI',
    ogTitle: 'Raisy - AI Workforce Platform | A Department of AI',
    ogDescription:
      "Raisy deploys a governed AI workforce with one shared memory — no forgotten context, human review first, autonomy earned through proven performance. A department's workload, directed by your people.",
    ogImage: 'https://raisy.team/og-image.jpg',
    ogType: 'website',
    canonicalUrl: 'https://raisy.team/',
  });

  return (
    <PublicLayout>
      {/* Hero — The Raise */}
      <HeroSection />

      {/* The gate — how long the closed beta stays open, immediately under the hero so the
          deadline is impossible to miss. Same timer as the announcement post; both read
          LIVE_GATE, so the date is stated once (and the button hides itself once it passes). */}
      <section className="bg-slate-50 pb-4 dark:bg-slate-950">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <CountdownTimer
            target={LIVE_GATE.target}
            kicker={LIVE_GATE.kicker}
            closedMessage={LIVE_GATE.closedMessage}
            actionHref={LIVE_GATE.postPath}
            actionLabel="Join the closed beta"
          />
        </div>
      </section>

      {/* The Raise — from URL to fundraising machine */}
      <RaiseSection />

      {/* Research — Why Fundraising Needs a Governed Workforce */}
      <ProblemResearchSection />

      {/* Beyond the Raise — the rest of the company, same engine */}
      <section className="bg-slate-50 dark:bg-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 rounded-full text-sm font-medium border border-cyan-200 dark:border-cyan-800 mb-4">
            Beyond the Raise
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            The Rest of Your Company, Same Engine
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            The same governed workforce that runs your raise — sales, recruiting, support, IR — runs every
            department. Nothing you build is wasted; everything you correct is remembered.
          </p>
        </div>
      </section>

      {/* How It Works — URL to AI Workforce */}
      <section className="bg-slate-50 dark:bg-slate-950">
        <CompanyBuilderFlow />
      </section>

      {/* By Department — Time Savings */}
      <section className="bg-slate-50 dark:bg-slate-950">
        <DepartmentCards />
      </section>

      {/* The Raise Ladder — interactive funding-stages explorer */}
      <section className="bg-slate-50 py-20 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-8 max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-medium text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400">
              The Raise Ladder
            </div>
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white md:text-5xl">
              See Your Stage, Set Your Raise
            </h2>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
              Where are you on the funding ladder? Click your stage, drag your round and ticket —
              Raisy turns the answer into a stage-matched investor list.
            </p>
          </div>
          <FundingStagesExplorer compact />
        </div>
      </section>

      {/* The Math — Fully Staffed vs Raisy */}
      <section className="bg-slate-50 dark:bg-slate-950">
        <CostComparisonChart />
      </section>

      {/* Trust Through Performance — Bot Graduation */}
      <section className="bg-slate-50 dark:bg-slate-950">
        <BotGraduationTimeline />
      </section>

      {/* Compounding Value */}
      <section className="bg-slate-50 dark:bg-slate-950">
        <ROIGrowthChart />
      </section>

      {/* Core Capabilities */}
      <FeaturesSection />

      {/* Compliance & Audit */}
      <ComplianceSection />

      {/* How The System Learns */}
      <SystemLearnsSection />

      {/* From the Blog — seed posts, visible on the landing page */}
      <BlogSection />

      {/* CTA */}
      <CTASection />
    </PublicLayout>
  );
};
